import React, { useEffect, useState } from "react";
import { Copy, Check } from "lucide-react";
import { useGetDefaultApiKeyV2LazyQuery } from "store/graphql/codegen/auto-gen/mgmt-api";
import { mgmtClient } from "store/graphql/apollo/clients";
import { getProductId, getEnvironmentId } from "utils/localStorage";
import { useDemoMode } from "../../../context/DemoModeContext";

const DEMO_API_KEY = "ts_demo_key_acme_labs_xxxxxxxxxxxx";

/**
 * Shows the current environment's default API key in the sidebar,
 * matching SetupTelemetry's left-nav "Environment & Api Key" section.
 */
export function SidebarApiKey() {
    const [apiKey, setApiKey] = useState<string>("");
    const [copied, setCopied] = useState(false);
    const { isDemoMode } = useDemoMode();

    const [GetDefaultApiKeyV2] = useGetDefaultApiKeyV2LazyQuery({
        client: mgmtClient,
        fetchPolicy: "no-cache",
    });

    const fetchKey = () => {
        if (isDemoMode) {
            setApiKey(DEMO_API_KEY);
            return;
        }
        const productId = getProductId() as string;
        const envId = getEnvironmentId() as string;
        if (!productId || !envId) return;
        GetDefaultApiKeyV2({
            variables: { productId, envId },
            fetchPolicy: "network-only",
            onCompleted: (data: any) => {
                if (data?.getDefaultAPIKeyV2?.key) {
                    setApiKey(data.getDefaultAPIKeyV2.key);
                }
            },
        });
    };

    useEffect(() => {
        fetchKey();
        window.addEventListener("environment_change", fetchKey);
        return () => window.removeEventListener("environment_change", fetchKey);
    }, [isDemoMode]);

    const handleCopy = () => {
        if (!apiKey) return;
        navigator.clipboard.writeText(apiKey);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const truncated = apiKey.length > 16
        ? `${apiKey.slice(0, 7)}...${apiKey.slice(-7)}`
        : apiKey;

    return (
        <div className="group relative flex items-center justify-between gap-1 px-2 py-1.5 bg-white border border-gray-200 rounded-lg text-xs">
            <span className="font-mono text-gray-700 truncate flex-1">
                {apiKey ? truncated : <span className="text-gray-400 italic">Loading…</span>}
            </span>
            <button
                onClick={handleCopy}
                disabled={!apiKey}
                className="flex-shrink-0 text-gray-400 hover:text-gray-700 disabled:opacity-40 transition-colors"
                title="Copy API key"
            >
                {copied
                    ? <Check className="w-3.5 h-3.5 text-green-600" />
                    : <Copy className="w-3.5 h-3.5" />
                }
            </button>
            {/* Full key tooltip */}
            {apiKey && (
                <div className="absolute left-0 bottom-full mb-1.5 px-2 py-1 bg-gray-800 text-white text-[10px] rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50 whitespace-nowrap pointer-events-none">
                    {apiKey}
                </div>
            )}
        </div>
    );
}
